import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

interface PrayerRequest {
  id: number;
  title: string;
  content: string;
  is_anonymous: boolean;
  created_at: string;
}

interface UserPrayerDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserPrayerDialog = ({
  userId,
  userName,
  open,
  onOpenChange,
}: UserPrayerDialogProps) => {
  const [prayers, setPrayers] = useState<PrayerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      fetchPrayerRequests();
    }
  }, [open, userId]);

  const fetchPrayerRequests = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("prayer_requests")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPrayers(data || []);
    } catch (error) {
      console.error("Error fetching prayer requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-app-surface border-app-border">
        <DialogHeader>
          <DialogTitle className="text-app-text-primary">
            Prayer Requests - {userName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : prayers.length === 0 ? (
          <div className="py-12 text-center text-app-text-secondary">
            No prayer requests found
          </div>
        ) : (
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-4">
              {prayers.map((prayer) => (
                <div
                  key={prayer.id}
                  className="p-4 rounded-lg border border-app-border bg-app-surface/50 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-app-text-primary">
                      {prayer.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      {prayer.is_anonymous && (
                        <Badge variant="secondary">Anonymous</Badge>
                      )}
                      <span className="text-xs text-app-text-secondary whitespace-nowrap">
                        {new Date(prayer.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <p className="text-app-text-secondary whitespace-pre-wrap">
                    {prayer.content}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
